import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HttpProject, ProjectOutline } from "tekkr-common/dist/model/project/project";
import { GanttChartIcon, InfoIcon } from "lucide-react";
import { DateTime } from "luxon";
import { ClientInferResponseBody } from "@ts-rest/core";
import { renderToStaticMarkup } from "react-dom/server";
import { AssistantLoadingIndicator, MessageContainer } from "../../components/shared/chat/message";
import { DatePicker } from "../../components/ui/date-picker";
import { Button } from "../../components/ui/button";
import { ProjectProvider, useProject } from "../../data/project";
import Spinner from "../../components/ui/spinner";
import { ProjectWorkstreamIcon } from "../../components/shared/project/workstream-header";
import { DeliverableIcon } from "../../components/shared/project/deliverable-card";
import { ContentSpacer } from "../common/layout";
import { ProjectPageHeader } from "../project/components/project-page-header";
import { useApplyTimelinesMutation } from "../../data/mutations/apply-timeline";
import { useTimelineDraftQuery } from "../../data/queries/timeline-draft";
import { apiContract } from "tekkr-common/dist/model/api/api.contract";
import { GanttChart, GanttChartApi } from "../../components/shared/project/gantt-chart";
import { GanttTaskInput, GanttTaskOutput } from "../../components/shared/project/gantt-chart-types";

type Project = HttpProject;

function InitialTimelineInputMessage({
    project,
    locked,
    onDatePicked,
}: {
    project: Project;
    locked: boolean;
    onDatePicked: (date: Date) => void;
}) {
    const [date, setDate] = useState<Date | undefined>(undefined);
    return (
        <MessageContainer role={"assistant"}>
            <p className={"font-semibold"}>Hello again!</p>
            <p>
                Let's figure out the timeline for your project &quot;{project.name}&quot;. Could you provide me with a
                rough idea of a date by which you would like to see this project completed?
            </p>
            <div className={"mt-4 flex flex-col gap-1"}>
                <p className={"text-sm font-medium text-muted-foreground"}>Ideal Delivery Date</p>
                <div className={"flex flex-row gap-3"}>
                    <DatePicker disabled={locked} date={date} onDateChanged={setDate} fromDate={new Date()} />
                    {!locked && (
                        <Button disabled={!date} onClick={() => onDatePicked(date!)}>
                            Continue
                        </Button>
                    )}
                </div>
            </div>
            {!locked && (
                <p className={"mt-2 text-sm font-medium text-muted-foreground"}>
                    No worries, you will be able to adjust this later, this is just a starting point for me.
                </p>
            )}
        </MessageContainer>
    );
}

function GanttMessage(props: { deliverables: DraftTimelineDeliverable[] }) {
    const ganttApiRef = useRef<GanttChartApi>(null);

    const { project } = useProject();
    const navigate = useNavigate();
    const applyTimelineMutation = useApplyTimelinesMutation({
        onSuccess: () => {
            navigate(`/project/${project.id}`, { replace: true });
        },
    });

    const tasks = useMemo((): GanttTaskInput[] => {
        function daysSince(days: number) {
            return DateTime.now().plus({ days }).toISODate()!;
        }
        return props.deliverables.map((d) => ({
            id: `${d.workstreamId}::${d.deliverableId}`,
            name: d.displayName,
            start: daysSince(d.offsetDays),
            end: daysSince(d.offsetDays + d.durationDays),
            progress: 0,
            workstreamId: d.workstreamId,
            deliverableId: d.deliverableId,
        }));
    }, [props.deliverables]);

    const ganttOptions = useMemo(
        () => ({
            readonly: false,
            popup: ({ task }: { task: GanttTaskOutput }) => {
                if (!task?.id) return "";

                const [wsId, deliverableId] = task.id.split("::");
                const ws = project.outline.workstreams.find((w) => w.id === wsId);
                const deliverable = ws?.deliverables.find((d) => d.id === deliverableId);
                if (!ws || !deliverable) return "";

                return renderToStaticMarkup(<Popup task={task} projectOutline={project.outline} />);
            },
        }),
        [project.outline]
    );

    function submit() {
        if (!ganttApiRef.current) {
            throw new Error("Gantt API is not initialized");
        }
        const updatedTasks = ganttApiRef.current.getTasks();
        applyTimelineMutation.mutate({
            updates: updatedTasks.map((task) => ({
                startDate: DateTime.fromJSDate(task._start).toISODate()!,
                workstreamId: task.workstreamId,
                deliverableId: task.deliverableId,
                endDate: DateTime.fromJSDate(task._end).toISODate()!,
            })),
        });
    }

    return (
        <MessageContainer role={"assistant"}>
            Great, thanks. Here's a timeline for all deliverables in this project, please adjust it as you see fit:
            <div
                className={
                    "mt-3 flex flex-row items-center gap-2 rounded-md border bg-accent p-2 text-sm font-medium opacity-65"
                }
            >
                <InfoIcon className={"h-5 w-5"} />
                You can adjust the timeline by dragging the items to the left or right. Change their duration by
                dragging the edges.
            </div>
            <div className={"mt-4 h-[70vh]"}>
                <div className={"absolute left-[5vw] h-[70vh] w-[90vw] rounded-md"}>
                    <GanttChart ref={ganttApiRef} tasks={tasks} ganttOptions={ganttOptions} />
                </div>
            </div>
            <div className={"mt-4 flex flex-col items-start gap-2"}>
                <p>
                    If you are happy with the timeline, please click the button below to apply it to the project. This
                    will update the timelines for all deliverables in this project.
                </p>
                <p>This doesn't need to be perfect yet, it should be a baseline for us to get started with.</p>
                <Button className={"mt-2"} disabled={applyTimelineMutation.isPending} onClick={submit}>
                    {applyTimelineMutation.isPending ? (
                        <Spinner className={"me-2 h-5 w-5"} />
                    ) : (
                        <GanttChartIcon className={"me-2 h-5 w-5"} />
                    )}
                    Apply this timeline
                </Button>
                <p className={"text-sm font-medium text-muted-foreground"}>
                    You can adjust the timelines for individual deliverables later.
                </p>
            </div>
        </MessageContainer>
    );
}

function Chat() {
    const { project } = useProject();
    const [queryParams, setQueryParams] = useSearchParams();
    const initialTargetDate = queryParams.get("initial-target-date");
    const timelineDraftQuery = useTimelineDraftQuery({
        enabled: !!initialTargetDate,
    });

    function onDatePicked(date: Date) {
        queryParams.set("initial-target-date", DateTime.fromJSDate(date).toISODate()!);
        setQueryParams(queryParams, { replace: true });
    }

    return (
        <div className={"flex flex-col gap-8"}>
            <InitialTimelineInputMessage onDatePicked={onDatePicked} project={project} locked={!!initialTargetDate} />
            {timelineDraftQuery.isFetching && !timelineDraftQuery.data && <AssistantLoadingIndicator />}
            {initialTargetDate && timelineDraftQuery.data && (
                <GanttMessage
                    deliverables={squeezeDeliverables(timelineDraftQuery.data, new Date(initialTargetDate!))}
                />
            )}
        </div>
    );
}

type DraftTimelineDeliverable = ClientInferResponseBody<
    typeof apiContract.generateDraftProjectTimeline,
    200
>["deliverables"][number];
function squeezeDeliverables(deliverables: DraftTimelineDeliverable[], targetDate: Date): DraftTimelineDeliverable[] {
    const daysUntilTarget = DateTime.fromJSDate(targetDate).diffNow("days").days;
    const lastDeliverable = deliverables[deliverables.length - 1];
    const squeezeFactor = (daysUntilTarget - lastDeliverable.durationDays) / lastDeliverable.offsetDays;
    return deliverables.map((d) => ({
        ...d,
        offsetDays: Math.round(d.offsetDays * squeezeFactor),
    }));
}

function Popup({ task, projectOutline }: { task: { id: string }; projectOutline: ProjectOutline }) {
    const [wsId, deliverableId] = task.id.split("::");
    const wsIndex = projectOutline.workstreams.findIndex((w) => w.id === wsId);
    const ws = projectOutline.workstreams[wsIndex]!;
    const dIndex = ws.deliverables.findIndex((d) => d.id === deliverableId);
    const deliverable = ws.deliverables[dIndex]!;
    return (
        <div className={"flex max-w-prose flex-col gap-3"}>
            <div className={"flex flex-row items-center gap-2 text-xs"}>
                <div className={"font-medium text-muted-foreground"}>Workstream:</div>
                <ProjectWorkstreamIcon ws={ws} index={wsIndex >= 0 ? wsIndex : undefined} size={"xs"} />
                <div>{ws.name}</div>
            </div>
            <hr />
            <div className={"flex flex-row items-center gap-2"}>
                <DeliverableIcon
                    deliverableId={deliverableId}
                    workstreamIndex={wsIndex >= 0 ? wsIndex : undefined}
                    deliverableIndex={dIndex >= 0 ? dIndex : undefined}
                />
                <div className={"text-sm font-medium"}>{deliverable.name}</div>
            </div>
            <p className={"text-sm"}>{deliverable.description}</p>
        </div>
    );
}

export function ProjectTimelineChatPage() {
    const { id: projectId } = useParams();
    if (!projectId) {
        throw new Error("projectId is required");
    }

    return (
        <ProjectProvider projectId={projectId}>
            <ContentSpacer contentWidth={"narrower"}>
                <div className={"flex flex-col gap-8"}>
                    <ProjectPageHeader preheader={"Timeline"} />
                    <Chat />
                </div>
            </ContentSpacer>
        </ProjectProvider>
    );
}
